import {
  AnchoredPanelType,
  FormInputGroupLayout,
  FormValues,
  getValue,
  InputFormGroup,
  InputFormSidePanel,
  InputFormSidePanelComponent,
} from "o4a-react";
import * as React from "react";
import apiClients from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { HeatwaveShapeSelect } from "../../components/HeatwaveShapeSelect/HeatwaveShapeSelect";
import { NodeCountEstimator } from "../../components/NodeCountEstimator/NodeCountEstimator";
import { parseId } from "../../helpers/idHelper";
import { useMutationCall } from "../../hooks/useMutationCall";
import {
  AsyncNotificationMethodKey,
  AsyncNotificationPolledResponseKey,
  MDS_HEATWAVE_CREATE_POLL_DELAY,
  MDS_HEATWAVE_CREATE_POLL_INTERVAL,
} from "../../models/AsyncNotificationProviders";
import { getOciRegion } from "../../utils";
import { OperationActionProps } from "../OperationTypes";

enum Fields {
  NodeCount = "nodeCount",
  Shape = "shape"
}

export interface MysqlCreateHeatWaveProps extends OperationActionProps {
  databaseId: string | undefined;
  clusterSize: number | undefined;
}

const MysqlCreateHeatWave = (
  { targetId, location, databaseId, clusterSize, onExecute, onCancel }: MysqlCreateHeatWaveProps,
): JSX.Element => {
  const { subscriptionId, resourceGroup, resourceName } = parseId(databaseId);
  const resourceGroupName = decodeURIComponent(resourceGroup || "");
  const dbSystemName = decodeURIComponent(resourceName || "");

  const [sidePanelRef, setSidePanelRef] = React.useState<InputFormSidePanelComponent>(
    {} as InputFormSidePanelComponent,
  );
  const { invokeCall: invokeCreate } = useMutationCall(
    { method: apiClients.withRegion(getOciRegion(location)).mdsDatabaseApi.addHeatWaveCluster },
  );

  const onCreate = (formValues: FormValues): void => {
    const shapeName: string = getValue(formValues, Fields.Shape, InputFormGroup)?.[0]?.id;
    const nodeCount: number = getValue(formValues, Fields.NodeCount, InputFormGroup);
    const invokeOptions = {
      onSuccess: onExecute,
      onFailure: sidePanelRef?.allowResubmit,
      notification: {
        inProgress: {
          title: Messages.notifications.inProgress.titles.createHeatWave(),
          message: Messages.notifications.inProgress.messages.createHeatWave(dbSystemName),
        },
        success: {
          title: Messages.notifications.success.titles.createHeatWave(),
          message: Messages.notifications.success.messages.createHeatWave(dbSystemName),
        },
        failure: {
          title: Messages.notifications.failure.titles.create(),
          message: Messages.notifications.failure.messages.createHeatWave(dbSystemName),
        },
        asyncPolling: {
          methodKey: AsyncNotificationMethodKey.MDS_HEATWAVE_GET,
          methodArgs: {
            subscriptionId,
            resourceGroupName,
            dbSystemName,
          },
          location,
          responseMethodKey: AsyncNotificationPolledResponseKey.MDS_HEATWAVE_CREATED_CHECK,
          delay: MDS_HEATWAVE_CREATE_POLL_DELAY,
          interval: MDS_HEATWAVE_CREATE_POLL_INTERVAL,
        },
      },
    };
    invokeCreate({
      subscriptionId,
      resourceGroupName,
      dbSystemName,
      addHeatWaveClusterDetails: {
        shapeName,
        clusterSize: nodeCount,
      },
    }, invokeOptions);
  };

  return (
    <InputFormSidePanel
      componentRef={setSidePanelRef}
      anchorId={targetId}
      type={AnchoredPanelType.LARGE}
      title={Messages.labels.createHeatWave()}
      onSubmit={onCreate}
      onClose={onCancel}
      layout={FormInputGroupLayout.COMPACT}
    >
      <div style={{ width: "300px" }}>
        <HeatwaveShapeSelect
          fieldName={Fields.Shape}
          label={Messages.labels.mySQLHeatwaveShape()}
          required
          subscriptionId={subscriptionId}
          location={location}
          tooltip={Messages.hints.selectAHeatwaveShape()}
        />
      </div>
      <NodeCountEstimator
        fieldName={Fields.NodeCount}
        subscriptionId={subscriptionId}
        resourceGroupName={resourceGroup}
        dbSystemName={dbSystemName}
        location={location}
        nodeCountDefaultValue={clusterSize}
      />
    </InputFormSidePanel>
  );
};

export const newMysqlCreateHeatWave = (
  props: MysqlCreateHeatWaveProps,
): JSX.Element => (<MysqlCreateHeatWave {...props} />);
